<template>
  <section
    ref="sectionRef"
    class="who-we-are"
    :class="{ 'who-we-are--visible': isVisible }"
    @mousemove="handleMouseMove"
    @mouseleave="resetImagePosition"
  >
    <div class="container">
      <div class="image-wrapper">
        <img
          :src="imageSrc"
          alt="Что такое HR3"
          class="image"
          :style="imageStyle"
        />
      </div>

      <div class="content">
        <span class="content-line"></span>

        <h2 class="title">
          Что такое Hr³<span class="question">?</span>
        </h2>

        <p class="description">
          Мы начинали как мобильное приложение Hr³ — простой инструмент
          для поиска работы и сотрудников. За нашими плечами нет инвесторов
          или крупных компаний — мы полностью независимый проект, сделанный
          на энтузиазме и опыте команды.
        </p>

        <p class="description description--secondary">
          Сейчас перед вами — наш веб-сервис. Мы активно его развиваем
          и дорабатываем, поэтому кое-где он ещё может быть сыроват.
          Но главное — мы честно делаем продукт, которым сами хотим
          пользоваться.
        </p>
      </div>
    </div>

    <div class="separator"></div>
  </section>
</template>

<script setup>
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
} from "vue";

import whoWeAreWhite from "../assets/who-we-are-white.webp";
import whoWeAreBlack from "../assets/who-we-are-dark.webp";

const sectionRef = ref(null);
const isVisible = ref(false);

const theme = ref(
  document.documentElement.getAttribute("data-theme") || "light"
);

const translateX = ref(0);
const translateY = ref(0);
const rotateX = ref(0);
const rotateY = ref(0);

const imageSrc = computed(() => {
  return theme.value === "dark"
    ? whoWeAreBlack
    : whoWeAreWhite;
});

const imageStyle = computed(() => ({
  transform: `
    perspective(1200px)
    translate3d(
      ${translateX.value}px,
      ${translateY.value}px,
      0
    )
    rotateX(${rotateX.value}deg)
    rotateY(${rotateY.value}deg)
  `,
}));

let themeObserver;
let visibilityObserver;

const handleMouseMove = (event) => {
  if (window.innerWidth <= 768) {
    return;
  }

  const section = sectionRef.value;

  if (!section) {
    return;
  }

  const rect = section.getBoundingClientRect();

  const relativeX =
    (event.clientX - rect.left) / rect.width;

  const relativeY =
    (event.clientY - rect.top) / rect.height;

  translateX.value = (relativeX - 0.5) * 8;
  translateY.value = (relativeY - 0.5) * 6;

  rotateY.value = (relativeX - 0.5) * 1.2;
  rotateX.value = (0.5 - relativeY) * 1;
};

const resetImagePosition = () => {
  translateX.value = 0;
  translateY.value = 0;
  rotateX.value = 0;
  rotateY.value = 0;
};

onMounted(() => {
  themeObserver = new MutationObserver(() => {
    theme.value =
      document.documentElement.getAttribute("data-theme") ||
      "light";
  });

  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme"],
  });

  visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      if (!entry.isIntersecting) {
        return;
      }

      isVisible.value = true;
      visibilityObserver?.disconnect();
    },
    {
      threshold: 0.18,
    }
  );

  if (sectionRef.value) {
    visibilityObserver.observe(sectionRef.value);
  }
});

onUnmounted(() => {
  themeObserver?.disconnect();
  visibilityObserver?.disconnect();
});
</script>

<style scoped>
.who-we-are {
  padding-top: 0;
  padding-bottom: 0;

  overflow: hidden;
}

.container {
  width: 1100px;
  height: 650px;

  margin: 0 auto;

  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row-reverse;
}

.image-wrapper {
  width: 800px;
  height: 650px;

  display: flex;
  align-items: center;
  justify-content: center;

  opacity: 0;
  transform: translateX(30px);

  transition:
    opacity 0.9s ease,
    transform 0.9s cubic-bezier(0.22, 1, 0.36, 1);
}

.who-we-are--visible .image-wrapper {
  opacity: 1;
  transform: translateX(0);
}

.image {
  width: 800px;
  height: 800px;

  object-fit: contain;

  margin-bottom: -10px;

  animation:
    image-breathing 6s ease-in-out infinite;

  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.3s ease;

  will-change: transform;
}

.image-wrapper:hover .image {
  filter:
    saturate(1.03)
    contrast(1.02);
}

.content {
  position: relative;

  max-width: 400px;

  padding-left: 22px;

  opacity: 0;
  transform: translateY(22px);

  transition:
    opacity 0.8s 0.12s ease,
    transform 0.8s 0.12s cubic-bezier(0.22, 1, 0.36, 1);
}

.who-we-are--visible .content {
  opacity: 1;
  transform: translateY(0);
}

.content-line {
  position: absolute;

  top: 6px;
  bottom: 4px;
  left: 0;

  width: 3px;

  border-radius: 999px;

  background:
    linear-gradient(
      180deg,
      #2277e6 0%,
      rgba(34, 119, 230, 0.08) 100%
    );

  transform: scaleY(0);
  transform-origin: top;

  transition:
    transform 0.8s 0.28s cubic-bezier(0.22, 1, 0.36, 1);
}

.who-we-are--visible .content-line {
  transform: scaleY(1);
}

.title {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;

  margin: 0 0 18px;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 32px;
  line-height: 1.05;
  letter-spacing: -1.2px;

  color: var(--text);
}

.question {
  color: #2277e6;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 1.18em;
  line-height: 1;

  display: inline-block;

  animation:
    question-float 3.5s ease-in-out infinite;
}

.description {
  margin: 0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  line-height: 1.65;
  letter-spacing: -0.3px;

  color:
    color-mix(
      in srgb,
      var(--text) 58%,
      transparent
    );
}

.description--secondary {
  margin-top: 14px;
}

.separator {
  width: 100%;
  height: 1px;

  margin-top: 80px;

  background-color: var(--separator_home);
}

@keyframes image-breathing {
  0%,
  100% {
    scale: 1;
  }

  50% {
    scale: 1.012;
  }
}

@keyframes question-float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-2px);
  }
}

@media (max-width: 1100px) {
  .container {
    width: calc(100% - 48px);
  }

  .image-wrapper {
    width: 65%;
  }

  .image {
    width: 100%;
    height: auto;
  }

  .content {
    width: 35%;
  }
}

@media (max-width: 768px) {
  .who-we-are {
    padding: 32px 0 0;
  }

  .container {
    width: 100%;
    height: auto;

    padding: 0 16px;

    flex-direction: column;

    gap: 22px;

    box-sizing: border-box;
  }

  .image-wrapper {
    width: 100%;
    height: auto;

    transform: translateY(20px);
  }

  .who-we-are--visible .image-wrapper {
    transform: translateY(0);
  }

  .image {
    width: 100%;
    max-width: 300px;
    height: auto;

    margin: 0 auto;

    transform: none !important;

    animation-duration: 7s;
  }

  .content {
    width: 100%;
    max-width: 100%;

    margin: 0;
    padding-left: 18px;
  }

  .title {
    margin-bottom: 14px;

    font-size: 24px;
    letter-spacing: -0.8px;
  }

  .description {
    max-width: 100%;

    margin: 0;

    font-size: 12px;
    line-height: 1.55;

    text-align: left;
  }

  .description--secondary {
    margin-top: 12px;
  }

  .separator {
    margin-top: 36px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .image,
  .question,
  .image-wrapper,
  .content,
  .content-line {
    animation: none !important;
    transition-duration: 0.01ms !important;
  }

  .image {
    transform: none !important;
  }
}
</style>